import { useState, type FC, useEffect, useRef } from 'react';
import cx from 'classnames';
import { Input } from 'antd';
import { Button } from '@g2u/ui-kit';
import { observer } from 'mobx-react-lite';

import { BookingStore } from 'store';
import { type TextAreaRef } from 'antd/es/input/TextArea';
import styles from './notepad-content.module.scss';

const InputSection: FC<{ idx: number; title?: string }> = observer((props) => {
  const { idx, title } = props;
  const { notes, setNotes } = BookingStore;
  const titleRef = useRef<TextAreaRef>(null);
  const descriptionRef = useRef<TextAreaRef>(null);
  const [titleValue, setTitleValue] = useState<string | undefined>(title);
  const [descriptionValue, setDescriptionValue] = useState<string>('');

  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  return (
    <div className={styles.notepadContent__textareaSection}>
      <Input.TextArea
        rows={1}
        autoSize
        ref={titleRef}
        onPressEnter={(e) => {
          descriptionRef.current?.focus();
          e.preventDefault();
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            if (e.currentTarget.selectionStart === e.currentTarget.value.length) {
              descriptionRef.current?.focus();
              e.preventDefault();
            }
          }
        }}
        value={titleValue}
        onChange={(e) => {
          setTitleValue(e.target.value);
          const newNotes = [...notes];
          newNotes[idx] = e.target.value;
          setNotes(newNotes);
        }}
        className={cx(styles.notepadContent__textarea_title, styles.notepadContent__textareaControl)}
        placeholder="Customer Name"
      />
      <Input.TextArea
        autoSize
        ref={descriptionRef}
        onKeyDown={(e) => {
          if (e.key === 'ArrowUp' && e.currentTarget?.selectionStart === 0) {
            titleRef.current?.focus();
            e.preventDefault();
          }
        }}
        className={cx(styles.notepadContent__textarea_description, styles.notepadContent__textareaControl)}
        placeholder="Problems and other quick notes."
        onChange={(e) => {
          setDescriptionValue(e.target.value);
        }}
      />
      <Button.$
        dataTestId="copy-notes"
        disabled={!descriptionValue}
        onClick={() => {
          setNotes([...notes, descriptionValue]);
        }}
        type={Button.Type.Link}
      >
        Add text to the Job Description
      </Button.$>
    </div>
  );
});

export const NotepadContent: FC = observer(() => {
  const [titleValue, setTitleValue] = useState<string>();
  const [fieldsAmount, setFieldsAmount] = useState<number>(1);

  useEffect(() => {
    fieldsAmount === 0 && setFieldsAmount(1);
  }, [fieldsAmount]);

  return (
    <div className={styles.notepadContent}>
      <div className={styles.notepadContent__title}>
        <div className={styles.notepadContent__titleText}>notepad</div>
        <Button.$
          type={Button.Type.Link}
          size={Button.Size.Small}
          className={styles.notepadContent__titleLink}
          dataTestId="clear-all-notepads"
          onClick={() => {
            setFieldsAmount(0);
            setTitleValue(''); // set to ignore 'name' from the store if cleared manually
          }}
        >
          Clear All
        </Button.$>
      </div>
      <div className={styles.notepadContent__textarea}>
        {[...Array(fieldsAmount).keys()].map((i) => (
          <InputSection key={i} idx={i} title={titleValue} />
        ))}
      </div>
      <Button.$
        type={Button.Type.Dashed}
        dataTestId="add-notepad-field"
        onClick={() => {
          setFieldsAmount(fieldsAmount + 1);
        }}
        className={styles.notepadContent__action}
      >
        + Add
      </Button.$>
    </div>
  );
});
